import React, { useState, useMemo } from 'react';
import { Server, Network, Wifi, Activity, Info } from 'lucide-react';
import { useTopology } from './TopologyContext';

// SVG viewBox dimensions — designed for 16:9-ish container
const VB_WIDTH = 1400;
const VB_HEIGHT = 620;

// Layout coordinates — three vertical tiers
const MASTER = { x: VB_WIDTH / 2, y: 60 };
const SWITCH_40G = { x: VB_WIDTH / 2, y: 200 };
const MGMT_HUB = { x: VB_WIDTH / 2, y: 340 };
const NODE_ROW_Y = 510;
const NODE_MARGIN_X = 80;

function NodeMap({ resources = [], network40g = {}, nodeUsage = {}, clusterCounts = {} }) {
  const { ALL_NODES, NODES_WITH_40G, NODE_TO_CLUSTER } = useTopology();
  const [hovered, setHovered] = useState(null);

  // Evenly-space all compute nodes across the row. Recomputes if topology changes.
  const NODE_POSITIONS = useMemo(() => {
    const slot = (VB_WIDTH - 2 * NODE_MARGIN_X) / ALL_NODES.length;
    return ALL_NODES.reduce((acc, node, i) => {
      acc[node] = {
        x: NODE_MARGIN_X + slot * (i + 0.5),
        y: NODE_ROW_Y,
      };
      return acc;
    }, {});
  }, [ALL_NODES]);

  const nodeStatus = useMemo(() => {
    const map = {};
    ALL_NODES.forEach(n => {
      const data = resources.find(r => r.node === n);
      const online = data && !data.error;
      const cpu = data?.cpu_usage || 0;
      const net = network40g?.[n];
      const totalGbps = net?.status === 'ok' ? (net.total_gbps || 0) : 0;
      const clusterId = NODE_TO_CLUSTER[n] || null;
      const usage = nodeUsage?.[n] || {};
      map[n] = { online, cpu, totalGbps, clusterId, data, net,
                 busy: !!usage.busy, job: usage.job || null, role: usage.role || null };
    });
    return map;
  }, [resources, network40g, nodeUsage, ALL_NODES, NODE_TO_CLUSTER]);

  const masterData = resources.find(r => r.node === 'master');
  const masterOnline = masterData && !masterData.error;

  const onlineCount = Object.values(nodeStatus).filter(s => s.online).length;
  const totalGbps = Object.values(nodeStatus).reduce((s, n) => s + n.totalGbps, 0);
  // Total running across both clusters (replaces per-node running sum)
  const totalRunning = Object.values(clusterCounts).reduce((s, c) => s + (c?.running || 0), 0);

  // Animated traffic — only show on 40G links when there's actual traffic
  const trafficLinks = ALL_NODES
    .filter(n => NODES_WITH_40G.has(n) && nodeStatus[n].totalGbps > 0.1)
    .map(n => ({ node: n, gbps: nodeStatus[n].totalGbps }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header strip with topology stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 16
      }}>
        <StatCard icon={Network} label="Master Status" value={masterOnline ? 'Online' : 'Offline'}
                  color={masterOnline ? '#22c55e' : '#ef4444'} />
        <StatCard icon={Server} label="Compute Nodes" value={`${onlineCount}/${ALL_NODES.length}`}
                  color={onlineCount === ALL_NODES.length ? '#22c55e' : '#eab308'} />
        <ClusterStatCard counts={clusterCounts} />
        <StatCard icon={Wifi} label="40G Aggregate" value={`${totalGbps.toFixed(1)} Gb/s`}
                  color={totalGbps > 10 ? '#22d3ee' : '#6b7280'} />
        <StatCard icon={Activity} label="Active Jobs" value={totalRunning}
                  color={totalRunning > 0 ? '#22c55e' : '#6b7280'} />
      </div>

      {/* Topology canvas */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 24,
        border: '1px solid #1e293b',
        padding: 16,
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 12px 12px',
          borderBottom: '1px solid #1e293b',
          marginBottom: 12
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Network style={{ width: 18, height: 18, color: '#a78bfa' }} />
            <h2 style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>Logical Network Topology</h2>
          </div>
          <Legend />
        </div>

        <svg
          viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Glow filter for active components */}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            {/* Animated traffic gradient */}
            <linearGradient id="traffic" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0" />
              <stop offset="50%" stopColor="#22d3ee" stopOpacity="1" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Section labels */}
          <text x={20} y={MASTER.y + 5} fill="#4b5563" fontSize="11" fontFamily="monospace" textAnchor="start">
            CONTROL PLANE
          </text>
          <text x={20} y={SWITCH_40G.y + 5} fill="#4b5563" fontSize="11" fontFamily="monospace" textAnchor="start">
            40G FABRIC
          </text>
          <text x={20} y={MGMT_HUB.y + 5} fill="#4b5563" fontSize="11" fontFamily="monospace" textAnchor="start">
            MGMT NETWORK
          </text>
          <text x={20} y={NODE_ROW_Y - 40} fill="#4b5563" fontSize="11" fontFamily="monospace" textAnchor="start">
            COMPUTE NODES
          </text>

          {/* === Cabling layer (drawn first, behind nodes) === */}

          {/* Master ↔ 40G switch (uplink) */}
          <line
            x1={MASTER.x} y1={MASTER.y + 30}
            x2={SWITCH_40G.x} y2={SWITCH_40G.y - 24}
            stroke={masterOnline ? '#3b82f6' : '#374151'}
            strokeWidth="2"
            strokeDasharray="4,4"
          />

          {/* Master ↔ Mgmt hub (separate management uplink) */}
          <path
            d={`M ${MASTER.x - 30} ${MASTER.y + 30} Q ${MASTER.x - 200} ${MGMT_HUB.y - 100} ${MGMT_HUB.x - 50} ${MGMT_HUB.y}`}
            stroke={masterOnline ? '#6b7280' : '#374151'}
            strokeWidth="1.5"
            fill="none"
            strokeDasharray="2,3"
          />

          {/* 40G switch → 40G nodes (the high-bandwidth fabric) */}
          {ALL_NODES.filter(n => NODES_WITH_40G.has(n)).map(n => {
            const pos = NODE_POSITIONS[n];
            const status = nodeStatus[n];
            const traffic = status.totalGbps;
            const linkColor = !status.online ? '#374151'
                            : traffic > 10 ? '#22d3ee'
                            : traffic > 0  ? '#0891b2'
                                           : '#1e40af';
            return (
              <g key={`fabric-${n}`}>
                <line
                  x1={SWITCH_40G.x} y1={SWITCH_40G.y + 24}
                  x2={pos.x} y2={pos.y - 30}
                  stroke={linkColor}
                  strokeWidth={status.online ? 3 : 1.5}
                  opacity={status.online ? 0.85 : 0.4}
                />
                {/* Animated traffic particle when there's actual flow */}
                {traffic > 0.5 && (
                  <circle r="4" fill="#22d3ee" opacity="0.9">
                    <animateMotion
                      dur={`${Math.max(0.6, 2.5 - traffic / 20)}s`}
                      repeatCount="indefinite"
                      path={`M ${SWITCH_40G.x},${SWITCH_40G.y + 24} L ${pos.x},${pos.y - 30}`}
                    />
                  </circle>
                )}
              </g>
            );
          })}

          {/* Mgmt hub → all compute nodes (1G management) */}
          {ALL_NODES.map(n => {
            const pos = NODE_POSITIONS[n];
            const status = nodeStatus[n];
            return (
              <line
                key={`mgmt-${n}`}
                x1={MGMT_HUB.x} y1={MGMT_HUB.y + 22}
                x2={pos.x} y2={pos.y - 28}
                stroke={status.online ? '#475569' : '#1e293b'}
                strokeWidth="1"
                strokeDasharray="3,3"
                opacity="0.5"
              />
            );
          })}

          {/* === Components (drawn over cables) === */}

          {/* MASTER node */}
          <Component
            x={MASTER.x} y={MASTER.y}
            label="MASTER"
            sublabel={masterData?.cpu_cores ? `${masterData.cpu_cores} cores` : 'Control'}
            online={masterOnline}
            color="#3b82f6"
            shape="rect"
            width={140} height={60}
            icon="server"
            onHover={() => setHovered({ type: 'master', data: masterData })}
            onLeave={() => setHovered(null)}
          />

          {/* 40G FABRIC SWITCH */}
          <Component
            x={SWITCH_40G.x} y={SWITCH_40G.y}
            label="40G FABRIC"
            sublabel="Mellanox · 9 ports active"
            online={onlineCount > 0}
            color="#22d3ee"
            shape="rect"
            width={220} height={48}
            icon="switch"
            highlight={trafficLinks.length > 0}
            onHover={() => setHovered({ type: 'switch40g' })}
            onLeave={() => setHovered(null)}
          />

          {/* Management hub */}
          <Component
            x={MGMT_HUB.x} y={MGMT_HUB.y}
            label="MGMT SWITCH"
            sublabel="1G management network"
            online={onlineCount > 0}
            color="#6b7280"
            shape="rect"
            width={200} height={44}
            icon="switch"
            small
            onHover={() => setHovered({ type: 'mgmt' })}
            onLeave={() => setHovered(null)}
          />

          {/* COMPUTE NODES */}
          {ALL_NODES.map(n => {
            const pos = NODE_POSITIONS[n];
            const status = nodeStatus[n];
            return (
              <ComputeNode
                key={n}
                x={pos.x} y={pos.y}
                node={n}
                status={status}
                onHover={() => setHovered({ type: 'node', node: n, status })}
                onLeave={() => setHovered(null)}
              />
            );
          })}
        </svg>

        {/* Hover detail panel */}
        {hovered && <HoverPanel hovered={hovered} />}

        {/* Footer */}
        <div style={{
          padding: '12px 12px 4px',
          borderTop: '1px solid #1e293b',
          marginTop: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 11,
          color: '#6b7280'
        }}>
          <Info style={{ width: 12, height: 12 }} />
          <span>Hover over any component for live details. Animated cyan particles show 40G fabric traffic in real time.</span>
        </div>
      </div>

      {/* Detail explanation card */}
      <div style={{
        backgroundColor: '#0f172a',
        borderRadius: 24,
        border: '1px solid #1e293b',
        padding: 24
      }}>
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Network & Cluster Design</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <DesignNote
            title="Flexible clusters, one fabric"
            body="node9–node16 form four disjoint 2-node base clusters — A (node9-10), B (node11-12), C (node13-14), D (node15-16) — that match the Submit page. Jobs can also run on larger spans: E (node9-13), F (node9-12), G (node13-16). node17 is the dedicated post-processing node. All share the same 40G Mellanox RoCE fabric for inter-node MPI."
          />
          <DesignNote
            title="40G fabric"
            body="All 9 compute nodes share the 40G Mellanox fabric for inter-node MPI traffic during PowerFlow CFD simulations. Lattice Boltzmann solvers exchange domain boundaries every timestep — bandwidth here directly affects time-to-solution."
          />
          <DesignNote
            title="Management network"
            body="A separate 1G network for control-plane traffic: SSH sessions, queue file polling, monitoring telemetry, and node administration. Keeping it off the fabric prevents management chatter from competing with simulation data."
          />
          <DesignNote
            title="Master node"
            body="Hosts the monitoring backend and this dashboard. Reads queue files for both clusters from node9 over NFS. Has its own 40G uplink for shared storage access plus a management interface for node control."
          />
        </div>
      </div>
    </div>
  );
}

// ===== Sub-components =====

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{
      backgroundColor: '#0f172a',
      borderRadius: 16,
      border: '1px solid #1e293b',
      padding: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        backgroundColor: `${color}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <Icon style={{ width: 18, height: 18, color }} />
      </div>
      <div>
        <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 2 }}>{label}</p>
        <p style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>{value}</p>
      </div>
    </div>
  );
}

// Stat card showing per-cluster activity (replaces aggregate "Active Jobs" with breakdown)
function ClusterStatCard({ counts }) {
  const { CLUSTERS } = useTopology();
  return (
    <div style={{
      backgroundColor: '#0f172a',
      borderRadius: 16,
      border: '1px solid #1e293b',
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      gap: 4
    }}>
      <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>Cluster Activity</p>
      {Object.entries(CLUSTERS).map(([cid, c]) => {
        const cnt = counts[cid] || { running: 0, pending: 0 };
        return (
          <div key={cid} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: c.color }} />
            <span style={{ color: c.color, fontWeight: 700, minWidth: 18 }}>{cid}</span>
            <span style={{ color: '#fff' }}>
              <span style={{ fontWeight: 700 }}>{cnt.running}</span> running
            </span>
            <span style={{ color: '#6b7280' }}>·</span>
            <span style={{ color: '#9ca3af' }}>
              <span style={{ fontWeight: 600 }}>{cnt.pending}</span> queued
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Component({ x, y, label, sublabel, online, color, width, height, highlight, onHover, onLeave }) {
  const halfW = width / 2;
  const halfH = height / 2;
  const fill = online ? '#1e293b' : '#0f172a';
  const stroke = online ? color : '#374151';
  return (
    <g
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={x - halfW} y={y - halfH}
        width={width} height={height}
        rx="10"
        fill={fill}
        stroke={stroke}
        strokeWidth="2"
        filter={highlight ? 'url(#glow)' : undefined}
      />
      <text
        x={x} y={y - 4}
        fill={online ? '#fff' : '#6b7280'}
        fontSize="13"
        fontWeight="700"
        textAnchor="middle"
        fontFamily="monospace"
        letterSpacing="1"
      >
        {label}
      </text>
      <text
        x={x} y={y + 14}
        fill={online ? '#9ca3af' : '#4b5563'}
        fontSize="10"
        textAnchor="middle"
        fontFamily="monospace"
      >
        {sublabel}
      </text>
      {/* Status dot */}
      <circle
        cx={x + halfW - 10} cy={y - halfH + 10}
        r="4"
        fill={online ? '#22c55e' : '#ef4444'}
      >
        {online && (
          <animate
            attributeName="opacity"
            values="1;0.4;1"
            dur="2s"
            repeatCount="indefinite"
          />
        )}
      </circle>
    </g>
  );
}

function ComputeNode({ x, y, node, status, onHover, onLeave }) {
  const { NODES_WITH_40G, CLUSTERS } = useTopology();
  const has40g = NODES_WITH_40G.has(node);
  const cluster = status.clusterId ? CLUSTERS[status.clusterId] : null;
  const isHead = cluster && cluster.head === node;
  const width = 110;
  const height = 56;
  const fill = status.online ? '#1e293b' : '#0f172a';
  // Stroke now reflects cluster membership when online — was previously a flat 40G color
  const stroke = !status.online ? '#374151'
                : cluster ? cluster.color
                : '#3b82f6';
  const cpu = status.cpu;
  const cpuColor = cpu >= 90 ? '#ef4444' : cpu >= 70 ? '#f97316' : '#22c55e';

  return (
    <g
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{ cursor: 'pointer' }}
    >
      <rect
        x={x - width / 2} y={y - height / 2}
        width={width} height={height}
        rx="8"
        fill={fill}
        stroke={stroke}
        strokeWidth={isHead ? 2.5 : 1.5}
        filter={isHead ? 'url(#glow)' : undefined}
      />
      <text
        x={x} y={y - 12}
        fill={status.online ? '#fff' : '#6b7280'}
        fontSize="11"
        fontWeight="700"
        textAnchor="middle"
        fontFamily="monospace"
      >
        {node.toUpperCase()}
      </text>
      {has40g && (
        <text
          x={x - width / 2 + 6} y={y - height / 2 + 12}
          fill="#22d3ee"
          fontSize="8"
          fontWeight="700"
          fontFamily="monospace"
        >
          40G
        </text>
      )}
      {isHead && (
        <text
          x={x + width / 2 - 6} y={y - height / 2 + 12}
          fill="#fbbf24"
          fontSize="8"
          fontWeight="700"
          fontFamily="monospace"
          textAnchor="end"
        >
          HEAD
        </text>
      )}
      {status.online ? (
        <>
          {/* Mini CPU bar inside node */}
          <rect
            x={x - 38} y={y + 2}
            width="76" height="4"
            rx="2"
            fill="#0f172a"
          />
          <rect
            x={x - 38} y={y + 2}
            width={76 * Math.min(cpu / 100, 1)}
            height="4"
            rx="2"
            fill={cpuColor}
          />
          <text
            x={x} y={y + 18}
            fill="#9ca3af"
            fontSize="9"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {cpu.toFixed(0)}% CPU
            {cluster && (
              <tspan fill={cluster.color} fontWeight="700"> · {status.clusterId}</tspan>
            )}
          </text>
        </>
      ) : (
        <text
          x={x} y={y + 8}
          fill="#ef4444"
          fontSize="10"
          textAnchor="middle"
          fontFamily="monospace"
        >
          OFFLINE
        </text>
      )}
      {/* Status dot — green + pulsing when the node is running a job, dim when free */}
      <circle
        cx={x + width / 2 - 8} cy={y - height / 2 + 8}
        r="3.5"
        fill={!status.online ? '#ef4444' : status.busy ? '#22c55e' : '#475569'}
      >
        {status.online && status.busy && (
          <animate attributeName="opacity" values="1;0.35;1" dur="1.6s" repeatCount="indefinite" />
        )}
      </circle>
      {/* IN USE marker below the node */}
      {status.online && status.busy && (
        <text x={x} y={y + height / 2 + 13} fill="#22c55e" fontSize="8"
              fontWeight="700" textAnchor="middle" fontFamily="monospace" letterSpacing="0.5">
          IN USE
        </text>
      )}
    </g>
  );
}

function Legend() {
  const { CLUSTERS } = useTopology();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: '#6b7280', flexWrap: 'wrap' }}>
      {Object.entries(CLUSTERS).map(([cid, c]) => (
        <LegendItem key={cid} color={c.color} label={c.label} border />
      ))}
      <LegendItem color="#fbbf24" label="HEAD" badge />
      <LegendItem color="#22d3ee" label="40G fabric" />
      <LegendItem color="#475569" label="Mgmt link" dashed />
      <LegendItem color="#22c55e" label="Online" dot />
    </div>
  );
}

function LegendItem({ color, label, dashed, dot, border, badge }) {
  let marker;
  if (dot) {
    marker = <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }} />;
  } else if (border) {
    marker = <span style={{ width: 14, height: 10, borderRadius: 3, border: `2px solid ${color}`, backgroundColor: '#1e293b' }} />;
  } else if (badge) {
    marker = <span style={{
      fontSize: 8, fontWeight: 700, color, padding: '1px 4px', borderRadius: 3,
      backgroundColor: `${color}22`, fontFamily: 'monospace', letterSpacing: 0.4
    }}>HEAD</span>;
  } else {
    marker = <span style={{
      width: 16, height: 2,
      backgroundColor: dashed ? 'transparent' : color,
      borderTop: dashed ? `2px dashed ${color}` : 'none'
    }} />;
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {marker}
      <span>{label}</span>
    </span>
  );
}

function HoverPanel({ hovered }) {
  const { CLUSTERS } = useTopology();
  let content = null;

  if (hovered.type === 'node') {
    const { node, status } = hovered;
    const data = status.data || {};
    const net = status.net;
    const cluster = status.clusterId ? CLUSTERS[status.clusterId] : null;
    const isHead = cluster && cluster.head === node;

    content = (
      <>
        <h4 style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: 'monospace' }}>
          {node.toUpperCase()}
          {isHead && (
            <span style={{
              fontSize: 9, fontWeight: 700, color: '#fbbf24',
              backgroundColor: 'rgba(251,191,36,0.15)',
              padding: '2px 5px', borderRadius: 4, marginLeft: 8, letterSpacing: 0.5
            }}>HEAD</span>
          )}
        </h4>
        {status.online ? (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            <Row label="Status" value={
              status.busy
                ? <span style={{ color: '#22c55e', fontWeight: 700 }}>
                    IN USE{status.job ? ` · ${(status.job.match(/run\d+/i) || [''])[0] || shortName(status.job)}` : ''}
                  </span>
                : status.role === 'postproc'
                  ? <span style={{ color: '#f59e0b', fontWeight: 700 }}>Post-proc</span>
                  : <span style={{ color: '#64748b', fontWeight: 700 }}>Free</span>
            } />
            {cluster && (
              <Row label="Cluster" value={
                <span style={{ color: cluster.color, fontWeight: 700 }}>
                  {cluster.label}{isHead ? ' (head)' : ''}
                </span>
              } />
            )}
            <Row label="CPU" value={`${status.cpu.toFixed(1)}% · ${data.used_cores?.toFixed(0) ?? 0}/${data.cpu_cores ?? 0} phys cores`} />
            {data.cpu_threads ? (
              <Row label="SMT" value={`${data.threads_per_core ?? 2} threads/core · ${data.cpu_threads} logical`} />
            ) : null}
            <Row label="Memory" value={`${data.used_mem?.toFixed(1) ?? 0}/${data.total_mem?.toFixed(1) ?? 0} GB (${data.mem_usage?.toFixed(0) ?? 0}%)`} />
            {data.uptime && <Row label="Uptime" value={data.uptime} />}
            {data.load_avg != null && <Row label="Load avg" value={`${data.load_avg}${data.cpu_cores ? ` / ${data.cpu_cores}` : ''}`} />}
            {data.disk_used_pct != null && <Row label="Disk" value={`${data.disk_used_pct}% used`} />}
            {net?.status === 'ok' ? (
              <>
                <Row label="40G RX" value={`${net.rx_gbps?.toFixed(2) ?? 0} Gb/s`} />
                <Row label="40G TX" value={`${net.tx_gbps?.toFixed(2) ?? 0} Gb/s`} />
                {net.rdma_gbps != null && (
                  <Row label="↳ RDMA/RoCE" value={
                    <span style={{ color: net.rdma_gbps > 0.05 ? '#22c55e' : '#6b7280', fontWeight: 600 }}>
                      {net.rdma_gbps.toFixed(2)} Gb/s
                    </span>
                  } />
                )}
                {net.tcp_gbps > 0.05 && <Row label="↳ TCP/kernel" value={`${net.tcp_gbps.toFixed(2)} Gb/s`} />}
                {net.pf_procs > 0 && <Row label="PowerFlow procs" value={net.pf_procs} />}
              </>
            ) : (
              <Row label="40G fabric" value="No live data" />
            )}
          </div>
        ) : (
          <p style={{ color: '#ef4444', fontSize: 11 }}>Node offline / unreachable</p>
        )}
      </>
    );
  } else if (hovered.type === 'master') {
    const data = hovered.data || {};
    const online = data && !data.error;
    content = (
      <>
        <h4 style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: 'monospace' }}>
          MASTER
        </h4>
        {online ? (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>
            <Row label="Role" value="Control plane / queue / dashboard" />
            <Row label="CPU" value={`${data.cpu_usage?.toFixed(1) ?? 0}% (${data.cpu_cores ?? 0} phys cores${data.cpu_threads ? ` · ${data.cpu_threads} logical` : ''})`} />
            <Row label="Memory" value={`${data.used_mem?.toFixed(1) ?? 0}/${data.total_mem?.toFixed(1) ?? 0} GB`} />
            <Row label="Connects to" value="40G fabric + mgmt switch" />
          </div>
        ) : (
          <p style={{ color: '#ef4444', fontSize: 11 }}>Master offline</p>
        )}
      </>
    );
  } else if (hovered.type === 'switch40g') {
    content = (
      <>
        <h4 style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: 'monospace' }}>
          40G FABRIC SWITCH
        </h4>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          <Row label="Type" value="Mellanox (RoCE-capable)" />
          <Row label="Connected nodes" value="All 9 compute nodes" />
          <Row label="Use case" value="Inter-node MPI for PowerFlow CFD" />
          <Row label="Master uplink" value="Yes" />
        </div>
      </>
    );
  } else if (hovered.type === 'mgmt') {
    content = (
      <>
        <h4 style={{ color: '#fff', fontSize: 13, fontWeight: 700, marginBottom: 8, fontFamily: 'monospace' }}>
          MANAGEMENT SWITCH
        </h4>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          <Row label="Speed" value="1 Gb/s" />
          <Row label="Use" value="SSH, queue, monitoring polls" />
          <Row label="Connected" value="All 9 nodes + master" />
        </div>
      </>
    );
  }

  return (
    <div style={{
      position: 'absolute',
      top: 60,
      right: 24,
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      backdropFilter: 'blur(8px)',
      border: '1px solid #334155',
      borderRadius: 12,
      padding: 14,
      width: 280,
      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
      pointerEvents: 'none'
    }}>
      {content}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ color: '#d1d5db', fontFamily: 'monospace', maxWidth: '60%', textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}

function DesignNote({ title, body }) {
  return (
    <div style={{
      backgroundColor: '#1e293b',
      borderRadius: 12,
      padding: 14,
      borderLeft: '3px solid #7c3aed'
    }}>
      <h4 style={{ color: '#fff', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</h4>
      <p style={{ color: '#9ca3af', fontSize: 11, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

// Short fallback label for a run name that has no "runNNN" token.
function shortName(name) {
  return name && name.length > 14 ? name.slice(0, 14) + '…' : (name || '');
}

export default NodeMap;
